import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BRAND } from "@/data";
import PromoCountdown from "@/components/PromoCountdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Tarot", href: "#tarot" },
  { label: "Pricing", to: "/pricing" },
  { label: "Shop", href: "#shop" },
  { label: "My Readings", to: "/account" },
  { label: "Contact", href: "#contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [promo, setPromo] = useState(null);
  const [bannerOpen, setBannerOpen] = useState(
    () => sessionStorage.getItem("fy_banner_dismissed") !== "1"
  );
  const [promoOpen, setPromoOpen] = useState(
    () => sessionStorage.getItem("fy_promo_dismissed") !== "1"
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/reading-tiers`)
      .then((r) => {
        const p = (r.data || []).find((t) => t.promo && t.expires_at);
        if (p) setPromo(p);
      })
      .catch(() => {});
  }, []);

  const dismissBanner = () => {
    sessionStorage.setItem("fy_banner_dismissed", "1");
    setBannerOpen(false);
  };

  const dismissPromo = () => {
    sessionStorage.setItem("fy_promo_dismissed", "1");
    setPromoOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="site-header"
    >
      <div
        className={`transition-all duration-500 ${
          scrolled ? "bg-[#140B09]/85 backdrop-blur-xl border-b border-[#C9A24B]/15" : "bg-transparent border-b border-transparent"
        }`}
      >
        <AnimatePresence initial={false}>
          {promo && promoOpen ? (
            <motion.div
              key="halloween-promo-banner"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden bg-gradient-to-r from-[#C9A24B] via-[#E9D3A0] to-[#C9A24B]"
            >
              <Link
                to="/pricing"
                data-testid="promo-banner"
                className="block text-center py-2 px-10 text-[10px] sm:text-xs tracking-[0.22em] uppercase text-[#1F130E] hover:brightness-95 transition-all"
              >
                ✦ Halloween Special · {promo.duration} live chat · ${promo.price.toFixed(0)} — ends in{" "}
                <PromoCountdown expiresAt={promo.expires_at} className="font-display text-[11px] sm:text-sm" /> →
              </Link>
              <button
                type="button"
                onClick={dismissPromo}
                aria-label="Dismiss Halloween promo banner"
                data-testid="promo-banner-close"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#1F130E]/70 hover:text-[#1F130E] hover:bg-black/10 transition-all"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </motion.div>
          ) : bannerOpen ? (
            <motion.div
              key="free-reading-banner"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden bg-gradient-to-r from-[#9C2A52] to-[#C77E8A]"
            >
              <a
                href="#tarot"
                data-testid="free-reading-banner"
                className="block text-center py-2 px-10 text-[10px] sm:text-xs tracking-[0.22em] uppercase text-[#EFE3CC] hover:brightness-110 transition-all"
              >
                ✦ First time? Draw a card & claim your FREE 5-minute reading →
              </a>
              <button
                type="button"
                onClick={dismissBanner}
                aria-label="Dismiss free reading banner"
                data-testid="free-reading-banner-close"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#EFE3CC]/80 hover:text-[#EFE3CC] hover:bg-black/15 transition-all"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </motion.div>
          ) : null}
        </AnimatePresence>
        <nav className="mx-auto max-w-[1300px] px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="font-display uppercase tracking-[0.28em] text-sm text-[#EFE3CC] hover:text-[#E9D3A0] transition-colors">
            {BRAND.name}
          </a>

          <div className="hidden md:flex items-center gap-10">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="text-xs tracking-[0.22em] uppercase text-[#B7A488] hover:text-[#E9D3A0] transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="text-xs tracking-[0.22em] uppercase text-[#B7A488] hover:text-[#E9D3A0] transition-colors duration-300"
                >
                  {l.label}
                </a>
              )
            )}
          </div>

          <Link
            to="/pricing"
            data-testid="nav-book-button"
            className="group flex items-center gap-2 rounded-full border border-[#C9A24B]/45 px-5 py-2.5 text-xs tracking-[0.18em] uppercase text-[#EFE3CC] transition-all duration-300 hover:border-[#C9A24B] hover:bg-[#C9A24B]/10"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#C9A24B]" strokeWidth={1.5} />
            Book
          </Link>
        </nav>
      </div>
    </motion.header>
  );
}
